/* Which quality dimension a rule's failures lower, as the rule forms show it.
 *
 * **Mirrors `DEFAULT_DIMENSION_OF_KIND` in `core.rules`**, which is the authority: eight of the nine
 * kinds have a default the server fills when a version is published without one, and `custom_sql`
 * has none — the server refuses a custom SQL version that does not declare its dimension, because
 * nothing can infer what arbitrary SQL measures. So the form shows the default as a fact for the
 * eight, and asks for the ninth.
 *
 * Nothing maps to accuracy: it means "matches reality", which needs a reference source no rule kind
 * checks yet, so it is not offered as a choice either.
 */

import type { QualityDimension } from '@/shared/api/generated/rules';

import type { RuleKind } from '../api/rules';

export const DIMENSION_OF_KIND: Readonly<Partial<Record<RuleKind, QualityDimension>>> = {
  not_null: 'completeness',
  unique: 'uniqueness',
  accepted_values: 'validity',
  range: 'validity',
  pattern: 'validity',
  freshness: 'timeliness',
  row_count_anomaly: 'consistency',
  referential_integrity: 'integrity',
};

/** The dimensions a custom SQL rule may declare. Accuracy is excluded: nothing can measure it yet. */
export const CHOOSABLE_DIMENSIONS: readonly QualityDimension[] = [
  'completeness',
  'validity',
  'consistency',
  'uniqueness',
  'timeliness',
  'integrity',
];

/** The dimension a kind is scored under, or `null` when the author must choose (custom SQL). */
export function dimensionOfKind(kind: RuleKind): QualityDimension | null {
  return DIMENSION_OF_KIND[kind] ?? null;
}

/* A custom SQL rule's dimension, chosen when the rule is created and read back when its first version
 * is published. **Carried in the rule's tags**, because `POST /v1/rules` takes no dimension — the
 * dimension belongs to the version — and the same tag mechanism already carries a suggestion's id
 * across the same two calls, so a reload between creating and publishing does not lose the choice. */
const DIMENSION_TAG = 'dimension:';

export function dimensionTag(dimension: QualityDimension): string {
  return `${DIMENSION_TAG}${dimension}`;
}

export function dimensionFromTags(
  tags: readonly string[] | undefined,
): QualityDimension | null {
  for (const tag of tags ?? []) {
    if (!tag.startsWith(DIMENSION_TAG)) continue;
    const value = tag.slice(DIMENSION_TAG.length).trim();
    const found = CHOOSABLE_DIMENSIONS.find((dimension) => dimension === value);
    if (found !== undefined) return found;
  }
  return null;
}
